/**
 * Concordance et résolution des références de l'Atlas archéologique de Fillion.
 * 
 * Contexte historique :
 * - L'Atlas archéologique de la Bible a d'abord été publié par Fillion en 1883 (93 planches).
 * - Une seconde édition augmentée a été publiée en 1886 (118 planches).
 * - Dans ses commentaires bibliques ultérieurs (notamment les Livres des Rois et Chroniques),
 *   Fillion cite fréquemment les numéros de planches de l'édition augmentée de 1886.
 * - Le corpus iconographique numérisé disponible correspond aux 93 planches de l'édition de 1883.
 * 
 * Cette table de concordance réoriente automatiquement les renvois de l'édition 1886
 * vers la planche et la figure correspondante de l'édition 1883 afin que le lecteur
 * accède immédiatement à la bonne illustration historique.
 */

const ROMAN_MAP = {
  i: 1, v: 5, x: 10, l: 50, c: 100, d: 500, m: 1000
};

export function romanToInt(r) {
  if (!r) return 0;
  const s = String(r).trim().toLowerCase().replace(/[^ivxlcdm]/g, '');
  let total = 0;
  for (let i = 0; i < s.length; i++) {
    const v = ROMAN_MAP[s[i]];
    if (!v) return 0;
    const next = ROMAN_MAP[s[i + 1]] || 0;
    total += v < next ? -v : v;
  }
  return total;
}

const INT_ROMAN = [
  [100, 'C'], [90, 'XC'], [50, 'L'], [40, 'XL'],
  [10, 'X'], [9, 'IX'], [5, 'V'], [4, 'IV'], [1, 'I']
];

export function intToRoman(num) {
  let n = parseInt(num, 10);
  if (!n || n < 1) return '';
  let res = '';
  for (const [val, rom] of INT_ROMAN) {
    while (n >= val) {
      res += rom;
      n -= val;
    }
  }
  return res;
}

/**
 * Concordances spécifiques connues entre les citations de Fillion et l'Atlas de 1883 (93 planches).
 */
const SPECIFIC_CONCORDANCE = [
  {
    // Matthieu 27:28 : Manteau écarlate ou chlamyde militaire des soldats de Rome (sagum / paludamentum)
    match: (plate, fig, ctx) =>
      ((plate === 2 || plate === 1) && (/chlamyde|sagum|paludamentum|coccinea|manteau.*soldat|soldats.*rome|matthieu\s*27/i.test(ctx))),
    targetPlate: 1,
    targetFig: '10',
    originalPlate: 2,
    note: "La chlamyde ou sagum militaire des soldats romains (éd. 1883 pl. I fig. 10, citée pl. II, fig. 7 dans l'éd. 1886)"
  },

  {
    // Matthieu 8:11 : Divans de repas et banquets (Lectus tricliniaris)
    match: (plate, fig, ctx) =>
      ((plate === 22 || plate === 23) && (/divan|repas|banquet|recumbent|accubitus|triclini/i.test(ctx) || /6|1|3|4/.test(fig))) ||
      (plate === 18 && (/9|10|11|12/.test(fig) || /triclini|divan|repas/i.test(ctx))),
    targetPlate: 18,
    targetFig: '9, 10, 11, 12',
    originalPlate: 22,
    note: "Lectus tricliniaris et divans de repas orientaux (éd. 1883, citée pl. XXII, fig. 6 / pl. XXIII, fig. 1, 3, 4, 6 dans l'éd. 1886)"
  },
  {
    // Matthieu 8:28 & 27:60 : Sépulcres taillés dans le roc et pierre roulée (gôlāl) (Gérasa, Jérusalem)
    match: (plate, fig, ctx) =>
      ((plate === 29 || plate === 30) && (/s[eé]pulcre|tombe|habitation|d[eé]mon|g[eé]ras|gadara|pierre.*roul|g[oō]lal|matthieu\s*27/i.test(ctx) || /5|2|3/.test(fig))) ||
      (plate === 21 && (/5|6|7/.test(fig) || /tombeau.*roc|s[eé]pulcre/i.test(ctx))),
    targetPlate: 21,
    targetFig: '5, 6, 7',
    originalPlate: 29,
    note: "Tombeaux taillés dans le roc en Palestine et pierre roulée gôlāl (éd. 1883 pl. XXI fig. 5, 6, 7, citée pl. XXIX, fig. 5 / pl. XXX, fig. 2, 3, 5 dans l'éd. 1886)"
  },
  {
    // Matthieu 9:17 : Outres de peau pour le vin nouveau
    match: (plate, fig, ctx) =>
      (plate === 20 && (/outre|vin|peau|uter/i.test(ctx) || /10|13|17/.test(fig))) ||
      (plate === 15 && (/6|8/.test(fig) && /outre/i.test(ctx))),
    targetPlate: 15,
    targetFig: '6, 8',
    originalPlate: 20,
    note: "Outres de peau pour la conservation des liquides (éd. 1883, citée pl. XX, fig. 10, 13, 17 dans l'éd. 1886)"
  },
  {
    // Matthieu 10:10 : Besace pour les provisions de voyage (pera / havre-sac)
    match: (plate, fig, ctx) =>
      ((plate === 76 || plate === 78) && (/besace|sac\b|provision|pera\b/i.test(ctx) || /1|4/.test(fig))) ||
      (plate === 61 && (fig === '2' || /havre-sac|besace/i.test(ctx))),
    targetPlate: 61,
    targetFig: '2',
    originalPlate: 76,
    note: "Voyageur muni d'un havre-sac (pera) (éd. 1883, citée pl. LXXVI, fig. 1, 4 dans l'éd. 1886)"
  },
  {
    // Matthieu 10:10 : Sandales de l'Orient
    match: (plate, fig, ctx) =>
      plate === 6 && (/sandale|soulier|calceament/i.test(ctx) || /11|14|1|2|3/.test(fig)),
    targetPlate: 6,
    targetFig: '1, 2, 3',
    originalPlate: 6,
    note: "Sandales orientales et assyriennes (éd. 1883, citée pl. VI, fig. 11, 14 dans l'éd. 1886)"
  },
  {
    // Matthieu 10:10 : Bâton de voyage
    match: (plate, fig, ctx) =>
      ((plate === 76 || plate === 62) && (/b[aâ]ton|voyage|virga\b/i.test(ctx) || fig === '3')) ||
      (plate === 62 && (/1|2/.test(fig) || /b[aâ]ton/i.test(ctx))),
    targetPlate: 62,
    targetFig: '1, 2',
    originalPlate: 76,
    note: "Voyageurs munis de bâtons de route (éd. 1883, citée pl. LXXVI, fig. 3 dans l'éd. 1886)"
  },
  {
    // Matthieu 10:29 : L'as romain en billon
    match: (plate, fig, ctx) =>
      ((plate === 64 || plate === 51) && (/\bas\b|monnaie.*billon|centime/i.test(ctx) || /19|21/.test(fig))) ||
      (plate === 51 && (fig === '5' || /\bas\b/i.test(ctx))),
    targetPlate: 51,
    targetFig: '5',
    originalPlate: 64,
    note: "As romain en billon (éd. 1883, citée pl. LXIV, fig. 19, 21 dans l'éd. 1886)"
  },
  {
    // Genèse 8:15 : Le déluge (médaille d'Apamée et traditions chaldéennes d'Izdubar)
    match: (plate, fig, ctx) =>
      ((plate === 105 || plate === 110) && (/d[ée]luge|izdubar|no[ée]|arche|apam[ée]e/i.test(ctx))) ||
      (plate === 88 && fig === '4'),
    targetPlate: 88,
    targetFig: '4',
    originalPlate: 105,
    note: "Le déluge (médaille d'Apamée) et traditions antiques (éd. 1883, citée pl. CV, fig. 2 / pl. CX, fig. 1 dans l'éd. 1886)"
  },
  {
    // Genèse 21:27 & Exode 21:33 : Puits orientaux (Puits de Bersabé, margelles et abreuvoirs)
    match: (plate, fig, ctx) =>
      (plate === 38 && !/roue/i.test(ctx) && (/puits|citerne|abreuvoir|auge|margelle|bersab/i.test(ctx))) ||
      (plate === 29 && fig === '6' && /puits|bersab/i.test(ctx)),
    targetPlate: 29,
    targetFig: '6',
    originalPlate: 38,
    note: "Puits de Bersabé et abreuvoirs orientaux (éd. 1883, citée pl. XXXVIII, fig. 1-2 dans l'éd. 1886)"
  },
  {
    // Deutéronome 11:10 : Roues à puiser et irrigation en Égypte
    match: (plate, fig, ctx) =>
      (plate === 38 && (/roues?\s+à\s+puiser|puiser.*pied|pied.*arros/i.test(ctx) || (fig && /6|7/.test(fig) && /arros|puiser/i.test(ctx)))) ||
      (plate === 29 && (/3|4/.test(fig) || /roue.*puiser/i.test(ctx))),
    targetPlate: 29,
    targetFig: '3, 4',
    originalPlate: 38,
    note: "Roues hydrauliques et roues à puiser en Égypte et en Orient (éd. 1883, citée pl. XXXVIII, fig. 6-7 dans l'éd. 1886)"
  },
  {
    // Deutéronome 12:2 : Culte idolâtrique sous les arbres frondus et arbre sacré assyrien
    match: (plate, fig, ctx) =>
      (plate === 116 && (/lignum\s+frondosum|arbre.*frond|arbre.*sacr|bosquet/i.test(ctx) || (fig && /5|6/.test(fig)))) ||
      (plate === 89 && (/arbre.*sacr/i.test(ctx) || fig === '2')),
    targetPlate: 89,
    targetFig: '2',
    originalPlate: 116,
    note: "L'arbre sacré des Assyriens et cultes idolâtriques sous les arbres (éd. 1883, citée pl. CXVI, fig. 5-6 dans l'éd. 1886)"
  },
  {
    // Deutéronome 20:19-20 : Assyriens coupant des arbres en pays ennemi lors d'un siège
    match: (plate, fig, ctx) =>
      (plate === 85 && (/arbre|verger|oliveraie|hache|succides|si[eè]ge|arbores/i.test(ctx))) ||
      (plate === 66 && (/arbre|verger|oliveraie|hache/i.test(ctx) || fig === '15')),
    targetPlate: 66,
    targetFig: '15',
    originalPlate: 85,
    note: "Assyriens coupant des arbres en pays ennemi lors d'un siège (éd. 1883 pl. LXVI fig. 15, citée pl. LXXXV, fig. 1 dans l'éd. 1886)"
  },
  {
    // Deutéronome 22:8 : Toits plats et balustrades en Orient (Nazareth, Syrie, Égypte)
    match: (plate, fig, ctx) =>
      ((plate === 11 || plate === 12 || plate === 13) && (/toit|balustrade|parapet|murum\s+tecti|terr[ae]sse/i.test(ctx))) ||
      (plate === 9 && (/toit|maison\s+syrienne|nazareth/i.test(ctx) || (fig && /4|13/.test(fig)))),
    targetPlate: 9,
    targetFig: '4, 13',
    originalPlate: 11,
    note: "Toits plats des maisons syriennes et à Nazareth (éd. 1883 pl. IX fig. 4, 13, citée pl. XI, XII, XIII dans l'éd. 1886)"
  },
  {
    // Deutéronome 25:4 : Battage du blé par les bœufs et traîneau à triturer sur l'aire
    match: (plate, fig, ctx) =>
      ((plate === 34 || plate === 25) && (/tritur|fouler.*bl[ée]|battre.*bl[ée]|tra[îi]neau.*tritur|area/i.test(ctx))) ||
      ((plate === 26 || plate === 27) && (/tritur|battu.*b[œo]ufs|aire/i.test(ctx))),
    targetPlate: 27,
    targetFig: '4, 8',
    originalPlate: 34,
    note: "Le blé battu sous les pieds des bœufs et traîneaux à triturer sur l'aire (éd. 1883 pl. XXVII fig. 4, 8, citée pl. XXXIV / XXV dans l'éd. 1886)"
  },
  {
    // Deutéronome 26:2 : Corbeilles et paniers en Orient pour l'offrande des prémices
    match: (plate, fig, ctx) =>
      (plate === 17 && (/corbeille|panier|cartallum|tresser.*rameaux|pr[ée]mices/i.test(ctx))) ||
      (plate === 13 && (/corbeille|panier|cophinus|sporta/i.test(ctx) || (fig && /1|2|3|4|5/.test(fig)))),
    targetPlate: 13,
    targetFig: '3, 4, 5',
    originalPlate: 17,
    note: "Paniers et corbeilles égyptiens et orientaux (éd. 1883 pl. XIII fig. 3, 4, 5, citée pl. XVII, fig. 4-6 dans l'éd. 1886)"
  },
  {
    // Exode 1:9 : Arrosage au schadouf et roues d'irrigation en Égypte
    match: (plate, fig, ctx) =>
      (plate === 38 && (/schadouf|irrigation|canaux/i.test(ctx))) ||
      (plate === 30 && (/1|2|3/.test(fig) && /schadouf/i.test(ctx))),
    targetPlate: 30,
    targetFig: '1, 2, 3',
    originalPlate: 38,
    note: "Le schadouf ou instrument à puiser dans l'ancienne Égypte (éd. 1883, citée pl. XXXVIII, fig. 1, 3, 4 dans l'éd. 1886)"
  },
  {
    // Genèse 24:22 : Anneaux du nez (nézem) dans l'Orient
    match: (plate, fig, ctx) =>
      (plate === 6 && (/n[ée]zem|nez|narine/i.test(ctx))) ||
      (plate === 5 && (/1|2/.test(fig) && /nez|n[ée]zem/i.test(ctx))),
    targetPlate: 5,
    targetFig: '1, 2',
    originalPlate: 6,
    note: "Anneaux du nez (nézem) en Orient (éd. 1883, citée pl. VI, fig. 6, 7 dans l'éd. 1886)"
  },
  {
    // Genèse 24:64 & Genèse 29:23 : Grand voile nuptial et voiles enveloppant les femmes orientales
    match: (plate, fig, ctx) =>
      ((plate === 2 || plate === 3 || plate === 25) && (/voile.*nuptial|long voile|envelopp.*lia|envelopp.*orient|fianc[ée]es?\s+s'enveloppent/i.test(ctx))),
    targetPlate: 2,
    targetFig: '4, 5',
    originalPlate: 25,
    note: "Voiles orientaux et femmes couvertes du grand voile et du manteau (éd. 1883, citée pl. II, fig. 14 / pl. III, fig. 1 / pl. XXV, fig. 1-2 dans l'éd. 1886)"
  },
  {
    // Genèse 31:17 & 31:34 : Chameaux de voyage et grands palanquins pour femmes et enfants
    match: (plate, fig, ctx) =>
      ((plate === 77 || plate === 78) && (/chameau|[ée]migre|palanquin|coussin.*rideau|porter les femmes/i.test(ctx))) ||
      (plate === 62 && (/3|4|5|6/.test(fig) && /chameau|palanquin/i.test(ctx))),
    targetPlate: 62,
    targetFig: '3, 4, 5, 6',
    originalPlate: 78,
    note: "Palanquins portés par des chameaux et voyage en Orient (éd. 1883, citée pl. LXXVII, fig. 4 / pl. LXXVIII, fig. 1, 3 dans l'éd. 1886)"
  },
  {
    // Genèse 31:19, Osée 3:4 & Zacharie 10:1 : Théraphim et petites idoles domestiques de Laban
    match: (plate, fig, ctx) =>
      ((plate === 112 || plate === 114 || plate === 115 || plate === 116) && (/th[ée]raphim|laban|idoles? de laban|statuette|divination/i.test(ctx))) ||
      (plate === 92 && (fig === '9' || /th[ée]raphim/i.test(ctx))),
    targetPlate: 92,
    targetFig: '9',
    originalPlate: 115,
    note: "Théraphim ou petites idoles domestiques (éd. 1883, citée pl. CXV, fig. 6 dans l'éd. 1886)"
  },
  {
    // Exode 1:1 : Fabrication des briques et corvée en Égypte sous le fouet des inspecteurs
    match: (plate, fig, ctx) =>
      (plate === 49 && (/corv[ée]e|brique|fouet|travaillez sans rel[âa]che|inspecteur/i.test(ctx))) ||
      (plate === 37 && (/1|2/.test(fig) && /brique|corv[ée]e/i.test(ctx))),
    targetPlate: 37,
    targetFig: '1, 2',
    originalPlate: 49,
    note: "Fabrication des briques et corvée chez les Égyptiens (éd. 1883, citée pl. XLIX, fig. 3, 6 dans l'éd. 1886)"
  },
  {
    // Exode 2:3 : Construction d'une barque de papyrus enduite de bitume et de poix
    match: (plate, fig, ctx) =>
      ((plate === 7 || plate === 8) && (/barque|papyrus|bitumine|poix|panier.*jonc/i.test(ctx))) ||
      (plate === 57 && fig === '3'),
    targetPlate: 57,
    targetFig: '3',
    originalPlate: 7,
    note: "Construction d'une barque de papyrus en Égypte (éd. 1883, citée pl. VII, fig. 4 / pl. VIII, fig. 1, 3 dans l'éd. 1886)"
  },
  {
    // Exode 5:10 : Moisson égyptienne coupant le blé sous l'épi et laissant le chaume
    match: (plate, fig, ctx) =>
      (plate === 34 && (/[ée]pi|chaume|moisson|paille/i.test(ctx))) ||
      (plate === 26 && (/1|2/.test(fig) && /moisson|[ée]pi|chaume/i.test(ctx))),
    targetPlate: 26,
    targetFig: '1, 2',
    originalPlate: 34,
    note: "Scènes de moisson dans l'antique Égypte (éd. 1883, citée pl. XXXIV, fig. 5 dans l'éd. 1886)"
  },
  {
    // Exode 5:10, Exode 21:20 & Deutéronome 25:2-3 : La bastonnade égyptienne et châtiment par la verge
    match: (plate, fig, ctx) =>
      ((plate === 71 || plate === 72) && (/2|4|5/.test(fig) || /bastonnade|flagellati|verge|virga|verberari|plagis|ch[âa]timent/i.test(ctx))) ||
      (plate === 56 && (/12|13/.test(fig) || /bastonnade/i.test(ctx))),
    targetPlate: 56,
    targetFig: '12, 13',
    originalPlate: 71,
    note: "La bastonnade dans l'antique Égypte (éd. 1883, citée pl. LXXI, fig. 4 / pl. LXXII, fig. 2 dans l'éd. 1886)"
  },
  {
    // Exode 11:4 & Nombres 11:8 : Moulins à main, mortiers et préparation des aliments
    match: (plate, fig, ctx) =>
      ((plate === 20 || plate === 21) && (/1|2|3|8|11/.test(fig) || /esclave.*moudre|moudre le bl[ée]|moulins? [aà] (bras|main)|mortiers?|olla|frangebat mola/i.test(ctx))) ||
      (plate === 16 && (/9|10/.test(fig) || /femmes occup[ée]es [àa] moudre|moulin [àa] (bras|main)/i.test(ctx))),
    targetPlate: 16,
    targetFig: '9, 10',
    originalPlate: 20,
    note: "Moulin à bras, mortiers et broyage des aliments (éd. 1883, citée pl. XX / XXI dans l'éd. 1886)"
  },
  {
    // Exode 14:6 : Chars de guerre égyptiens attelés de deux chevaux
    match: (plate, fig, ctx) =>
      ((plate === 88 || plate === 99 || plate === 100) && (/chevaux rapides|pharaon.*armée|mont[ée]s par deux hommes|chars?.*guerre/i.test(ctx))) ||
      (plate === 69 && (/10|11/.test(fig) && /égyptien|pharaon/i.test(ctx))),
    targetPlate: 69,
    targetFig: '10, 11',
    originalPlate: 88,
    note: "Char de guerre égyptien attelé et monté (éd. 1883, citée pl. LXXXVIII, fig. 11-12 / pl. XCIX, fig. 6 / pl. C, fig. 7 dans l'éd. 1886)"
  },
  {
    // Lévitique 2:4 / Exode 8:1 / Exode 12:34 / Genèse 15:17 / Lévitique 26:26 : Four portatif oriental (ṭannur)
    match: (plate, fig, ctx) =>
      ((plate === 41 || plate === 42) && (/9|10|11/.test(fig) || /four|cliban|furnos|tannur|cuisson|pain|g[âa]teau/i.test(ctx))) ||
      (plate === 34 && (/4|5|6|7|8|9/.test(fig) && /four|cliban|furnos|tannur/i.test(ctx))) ||
      /four\s+portatif|clibano|coctum in clibano|\bṭannur\b|\btannur\b/i.test(ctx),
    targetPlate: 34,
    targetFig: '4, 5, 6',
    originalPlate: 42,
    note: "Fours orientaux et préparation des pains et gâteaux (éd. 1883, citée pl. XLII, fig. 9-11 dans l'éd. 1886)"
  },
  {
    // Lévitique 2:4 : Gâteaux piquetés (hallot) et galettes (lagana, r'qiqim)
    match: (plate, fig, ctx) =>
      (plate === 105 && (fig === '4' || /hallot|piquet[ée]|azyme|lagana|r['’]qiqim/i.test(ctx))) ||
      (plate === 80 && (fig === '10' || /azyme|hallot|lagana/i.test(ctx))) ||
      /hallot|pain.*piquet[ée]|lagana azyma/i.test(ctx),
    targetPlate: 80,
    targetFig: '10',
    originalPlate: 105,
    note: "Pains azymes et galettes (éd. 1883, citée pl. CV, fig. 4 dans l'éd. 1886)"
  },
  {
    // Lévitique 2:5 (1re réf.) / Ézéchiel 4:3 : Poêle à frire (sartago, makhăbat) — pl. XX, fig. 12 -> pl. XVIII, fig. 14
    match: (plate, fig, ctx) =>
      (plate === 20 && (fig === '12' || /sartag|po[êe]le|frit|frire|makhabat|makhăbat/i.test(ctx))) ||
      (plate === 18 && (fig === '14' || /po[êe]le|frire|sartag/i.test(ctx))),
    targetPlate: 18,
    targetFig: '14',
    originalPlate: 20,
    note: "Poêle à frire antique (éd. 1883, citée pl. XX, fig. 12 dans l'éd. 1886)"
  },
  {
    // Lévitique 2:5 (2e réf.) : On fait frire des gâteaux dans la poêle — pl. XLII, fig. 15 -> pl. XXXIV, fig. 3
    match: (plate, fig, ctx) =>
      (plate === 42 && (fig === '15' || /sartag|po[êe]le|frit|frire|makhabat|makhăbat/i.test(ctx))) ||
      (plate === 34 && (fig === '3' || /frire.*g[âa]teau/i.test(ctx))),
    targetPlate: 34,
    targetFig: '3',
    originalPlate: 42,
    note: "On fait frire des gâteaux à la poêle (éd. 1883, citée pl. XLII, fig. 15 dans l'éd. 1886)"
  },
  {
    // Exode 25:29 & Lévitique 24:6-7 & 1 Maccabées 1:21 : Table des pains de proposition, navettes d'encens et Arc de Titus
    match: (plate, fig, ctx) =>
      ((plate === 84 || plate === 104) && (/pains? de proposition|mensa.*propositionis|deux piles|super eos thus|petite coupe d'or|arc de triomphe de titus|table et ses ustensiles/i.test(ctx))) ||
      (plate === 80 && (/3|4|5|6|7|8/.test(fig) || /pains? de proposition/i.test(ctx))),
    targetPlate: 80,
    targetFig: '3, 4, 5, 6, 7, 8',
    originalPlate: 104,
    note: "Table des pains de proposition, navettes à encens et bas-relief de l'Arc de Titus (éd. 1883, citée pl. CIV, fig. 3, 5-9, 12 dans l'éd. 1886)"
  },
  {
    // Exode 25:31 : Le chandelier d'or à sept branches
    match: (plate, fig, ctx) =>
      (plate === 103 && (/7|10|11/.test(fig) || /tige.*calice|pommes et ses fleurs|ex ipso procedentia|chandelier/i.test(ctx))) ||
      (plate === 83 && (/3|8/.test(fig) && /chandelier/i.test(ctx))),
    targetPlate: 77,
    targetFig: '11',
    originalPlate: 103,
    note: "Le chandelier à sept branches (éd. 1883, citée pl. CIII, fig. 7, 10, 11 dans l'éd. 1886)"
  },
  {
    // Exode 25:37 : Lampes antiques (avec ouverture pour l'huile et bec pour la mèche)
    match: (plate, fig, ctx) =>
      (plate === 18 && (/m[èe]che|verser l'huile|super candelabrum|luceant|lampe|matthieu\s*25/i.test(ctx))) ||
      (plate === 14 && (/1|2|3/.test(fig) && /lampe/i.test(ctx))),
    targetPlate: 14,
    targetFig: '1, 2, 3',
    originalPlate: 18,
    note: "Lampes antiques d'Assyrie, d'Égypte et de Rome (éd. 1883, citée pl. XVIII, fig. 5, 8, 10, 14 dans l'éd. 1886)"
  },
  {
    // Exode 25:38 : Mouchettes et pinces pour préparer les lampes du candélabre
    match: (plate, fig, ctx) =>
      ((plate === 17 || plate === 18 || plate === 104) && (/mouch[ée]|m[èe]che|emunctori|pince|ustensiles.*cand[ée]labre/i.test(ctx))) ||
      (plate === 14 && (/11|12/.test(fig) || /mouchette|pince.*lampe/i.test(ctx))),
    targetPlate: 14,
    targetFig: '11, 12',
    originalPlate: 17,
    note: "Pinces et mouchettes antiques pour préparer les lampes (éd. 1883, citée pl. XVII, fig. 9 / pl. XVIII, fig. 9 / pl. CIV, fig. 1 dans l'éd. 1886)"
  },
  {
    // Exode 26:15 : Ais du Tabernacle (planches de bois d'acacia à deux tenons)
    match: (plate, fig, ctx) =>
      (plate === 95 && (fig === '4' || /setim|incastratur|tenon|mortaise|ais du tabernacle/i.test(ctx))) ||
      (plate === 75 && fig === '5'),
    targetPlate: 75,
    targetFig: '5',
    originalPlate: 95,
    note: "Ais du Tabernacle et leurs tenons (éd. 1883, citée pl. XCV, fig. 4 dans l'éd. 1886)"
  },
  {
    // Exode 26:18 : Quarante bases d'argent pour enclaver les ais du Tabernacle
    match: (plate, fig, ctx) =>
      (plate === 95 && (fig === '7' || /quadraginta bases|quarante bases|supports massifs/i.test(ctx))) ||
      (plate === 75 && (/3|4/.test(fig) && /base/i.test(ctx))),
    targetPlate: 75,
    targetFig: '3, 4',
    originalPlate: 95,
    note: "Bases pour enclaver les ais du Tabernacle (éd. 1883, citée pl. XCV, fig. 7 dans l'éd. 1886)"
  },
  {
    // Exode 26:26 : Charpente et barres transversales pour retenir les ais du Tabernacle
    match: (plate, fig, ctx) =>
      (plate === 95 && (/2.*5/.test(fig) || /cinqui[èe]me.*milieu|toute la longueur|30 ou 10 coud[ée]es|barres/i.test(ctx))) ||
      (plate === 75 && (/2|6/.test(fig) && /charpente|barre/i.test(ctx))),
    targetPlate: 75,
    targetFig: '2, 6',
    originalPlate: 95,
    note: "Charpente du Tabernacle et barres pour retenir les ais (éd. 1883, citée pl. XCV, fig. 2, 5 dans l'éd. 1886)"
  },
  {
    // Exode 26:33 & Exode 30:6 & Exode 30:17 & Lévitique 1:3 & Nombres 3:26, 3:38, 8:2-3 : Plan du Tabernacle (Saint, Saint des saints, voile, parvis et autel)
    match: (plate, fig, ctx) =>
      ((plate === 95 || plate === 96) && (/autre paroi|autel des parfums|derri[èe]re ce voile|en avant du voile|cour ext[ée]rieure.*autel|entre l'autel et l'entr[ée]e du saint|ostium tabernaculi|rideaux du parvis|autour de l'autel|double ligne de tentes|tabernacle qui servait de palais|paroi m[ée]ridionale|paroi septentrionale|candelabrum|table des pains/i.test(ctx))) ||
      (plate === 75 && fig === '1'),
    targetPlate: 75,
    targetFig: '1',
    originalPlate: 96,
    note: "Plan et charpente du Tabernacle, du Saint et du parvis (éd. 1883, citée pl. XCV, fig. 1, 5 / pl. XCVI, fig. 1, 2 dans l'éd. 1886)"
  },
  {
    // Exode 27:9 & Exode 27:17 : Enceinte et colonnes du parvis du Tabernacle
    match: (plate, fig, ctx) =>
      ((plate === 96 || plate === 97) && (/crochets des colonnes|tringles.*argent|soixante.*intervalle de cinq coud[ée]es|enceinte du tabernacle/i.test(ctx))) ||
      (plate === 76 && fig === '6'),
    targetPlate: 76,
    targetFig: '6',
    originalPlate: 97,
    note: "Détails de l'enceinte et des colonnes du Tabernacle (éd. 1883, citée pl. XCVI, fig. 1 / pl. XCVII, fig. 1-2 dans l'éd. 1886)"
  },
  {
    // Exode 27:1 & Lévitique 9:22 : Autel des holocaustes, sa grille et son plan incliné
    match: (plate, fig, ctx) =>
      ((plate === 78 || plate === 97 || plate === 98) && (/ignium receptacula|craticulam|grille en forme de treillis|autel des holocaustes|plan inclin[ée]|pontife supr[êe]me|cinq victimes/i.test(ctx))),
    targetPlate: 81,
    targetFig: '7',
    originalPlate: 98,
    note: "L'autel des holocaustes (éd. 1883, citée pl. XCVII, fig. 3-8 / pl. XCVIII, fig. 6 dans l'éd. 1886)"
  },
  {
    // Exode 28:26 (1re réf.) : Le rational, l'éphod et le grand costume du grand prêtre
    match: (plate, fig, ctx) =>
      (plate === 106 && (/rational.*éphod|v[êe]tements du grand pr[êe]tre|quatre anneaux/i.test(ctx))) ||
      (plate === 82 && (/1|2|3/.test(fig) && /grand pr[êe]tre|[ée]phod|rational/i.test(ctx))),
    targetPlate: 82,
    targetFig: '1, 2, 3',
    originalPlate: 106,
    note: "Grand prêtre en grand costume, éphod et rational (éd. 1883, citée pl. CVI, fig. 7, 11, 12 dans l'éd. 1886)"
  },
  {
    // Exode 28:26 (2e réf.) : Prêtre égyptien revêtu de l'éphod (analogies égyptiennes d'Ancessi)
    match: (plate, fig, ctx) =>
      (plate === 116 && (fig === '4' || /ancessi|analogies? [ée]gyptiennes?|v[êe]tements du grand pr[êe]tre/i.test(ctx))) ||
      (plate === 89 && fig === '5'),
    targetPlate: 89,
    targetFig: '5',
    originalPlate: 116,
    note: "Prêtre égyptien revêtu de l'éphod (éd. 1883, citée pl. CXVI, fig. 4 dans l'éd. 1886)"
  },
  {
    // Exode 28:31 : Robe de l'éphod (tunica superhumeralis) et grand costume du grand prêtre
    match: (plate, fig, ctx) =>
      (plate === 106 && (fig === '12' || /ora per gyrum|bord tiss[ée]|déchirer/i.test(ctx))),
    targetPlate: 82,
    targetFig: '1',
    originalPlate: 106,
    note: "Grand prêtre revêtu de la robe de l'éphod (éd. 1883, citée pl. CVI, fig. 12 dans l'éd. 1886)"
  },
  {
    // Exode 28:36 & Zacharie 3:5 : Lame d'or / Frontal du grand prêtre (Sainteté à Jéhovah)
    match: (plate, fig, ctx) =>
      (plate === 106 && (/9|11/.test(fig) && /lame d'or|ruban d'hyacinthe|tiare|plaque.*or|cidaris|saintet[ée]|frontal/i.test(ctx))) ||
      (plate === 82 && fig === '4'),
    targetPlate: 82,
    targetFig: '4',
    originalPlate: 106,
    note: "Frontal ou lame d'or du grand prêtre (éd. 1883, citée pl. CVI, fig. 9, 11 dans l'éd. 1886)"
  },
  {
    // Exode 28:40 (1re réf.) : Tuniques de lin des simples prêtres et petit costume du grand prêtre
    match: (plate, fig, ctx) =>
      (plate === 107 && (/5|6/.test(fig) || /tunicas lineas|tuniques de lin|balteos|tiaras/i.test(ctx))) ||
      (plate === 81 && (/2|3/.test(fig) && /pr[êe]tre/i.test(ctx))),
    targetPlate: 81,
    targetFig: '2, 3',
    originalPlate: 107,
    note: "Costume de lin d'un prêtre juif et du grand prêtre (éd. 1883, citée pl. CVII, fig. 5, 6 dans l'éd. 1886)"
  },
  {
    // Exode 28:40 (2e réf.) : Caleçons courts de lin à la manière égyptienne (feminalia linea)
    match: (plate, fig, ctx) =>
      ((plate === 1 || plate === 22) && (/feminalia|cale[çc]ons? de lin|mani[èe]re [ée]gyptienne/i.test(ctx))),
    targetPlate: 1,
    targetFig: '4, 5',
    originalPlate: 22,
    note: "Vêtements courts et ceintures à la manière antique (éd. 1883, citée pl. I, fig. 4 ; pl. XXII, fig. 3 dans l'éd. 1886)"
  },
  {
    // Exode 29:15 : Libations et vases à libations sur les victimes
    match: (plate, fig, ctx) =>
      (plate === 77 && (/10.*13/.test(fig) || /combustion des chairs|offeres totum arietem|libation/i.test(ctx))) ||
      (plate === 81 && fig === '4'),
    targetPlate: 81,
    targetFig: '4',
    originalPlate: 77,
    note: "Vases à libations (éd. 1883, citée pl. LXXVII, fig. 10, 13 dans l'éd. 1886)"
  },
  {
    // Exode 32:2 (1re réf.) : Pendants d'oreilles antiques (égyptiens et assyriens)
    match: (plate, fig, ctx) =>
      (plate === 4 && (fig === '2' || /pendants? d'oreilles/i.test(ctx))),
    targetPlate: 4,
    targetFig: '5, 6, 7',
    originalPlate: 4,
    note: "Antiques pendants d'oreilles assyriens et égyptiens (éd. 1883)"
  },
  {
    // Exode 32:2 (2e réf.) : Le bœuf Apis adoré en Égypte (veau d'or)
    match: (plate, fig, ctx) =>
      ((plate === 111 || plate === 91) && (/apis|b[œo]uf apis|veau d'or/i.test(ctx))),
    targetPlate: 91,
    targetFig: '8, 13, 14',
    originalPlate: 111,
    note: "Le bœuf Apis et génuflexion devant Apis en Égypte (éd. 1883, citée pl. CXI, fig. 5, 9, 11 dans l'éd. 1886)"
  },
  {
    // Lévitique 1:5 : Coupe pour recevoir et verser le sang des victimes autour de l'autel
    match: (plate, fig, ctx) =>
      (plate === 106 && (fig === '8' || /recevoir.*sang|coupe.*sang|per altaris circuitum/i.test(ctx))) ||
      (plate === 77 && fig === '3'),
    targetPlate: 77,
    targetFig: '3',
    originalPlate: 106,
    note: "Coupe pour recevoir et verser le sang des victimes (éd. 1883, citée pl. CVI, fig. 8 dans l'éd. 1886)"
  },
  {
    // Lévitique 1:8 : Membres des victimes portés et arrangés sur l'autel des holocaustes
    match: (plate, fig, ctx) =>
      (plate === 106 && (/10|13/.test(fig) || /membres.*coup[ée]s|reconstituant.*animal sur l'autel/i.test(ctx))) ||
      (plate === 82 && (/6|8/.test(fig) && /membres|victimes/i.test(ctx))),
    targetPlate: 82,
    targetFig: '6, 8',
    originalPlate: 106,
    note: "On porte les membres des victimes sur l'autel chargé de victimes (éd. 1883, citée pl. CVI, fig. 10, 13 dans l'éd. 1886)"
  },
  {
    // Lévitique 1:15 & 1:17 : Sacrifice d'oiseaux sur l'autel (monuments égyptiens)
    match: (plate, fig, ctx) =>
      (plate === 106 && (fig === '7' || /oiseau|colombe|tourterelle|ailes.*sans les s[ée]parer|monuments [ée]gyptiens/i.test(ctx))) ||
      (plate === 82 && fig === '5'),
    targetPlate: 82,
    targetFig: '5',
    originalPlate: 106,
    note: "Sacrifice d'oiseaux sur l'autel d'après les monuments égyptiens (éd. 1883, citée pl. CVI, fig. 7 dans l'éd. 1886)"
  },
  {
    // Lévitique 10:1 & Lévitique 16:12 & Apocalypse 8:3 : Encensoirs portatifs (thuribulis)
    match: (plate, fig, ctx) =>
      ((plate === 48 || plate === 78 || plate === 98 || plate === 108) && (/thuribul|encensoir|feu [ée]tranger/i.test(ctx))) ||
      ((plate === 76 || plate === 77) && fig === '7' && /encensoir|thuribul/i.test(ctx)),
    targetPlate: 77,
    targetFig: '7',
    originalPlate: 98,
    note: "Encensoir sacré du culte juif (éd. 1883, citée pl. XCVIII, fig. 7, 9 dans l'éd. 1886)"
  },
  {
    // Lévitique 13 & Lévitique 19 : Lépreux en Orient (Lépreux demandant l'aumône, tête et main de lépreux)
    match: (plate, fig, ctx) =>
      ((plate === 26 || plate === 85) && (/l[ée]pre|l[ée]preux|leprosus/i.test(ctx))) ||
      (plate === 19 && (/1|2|3/.test(fig) || /l[ée]preux/i.test(ctx))),
    targetPlate: 19,
    targetFig: '1, 2, 3',
    originalPlate: 26,
    note: "Lépreux demandant l'aumône, tête et main de lépreux (éd. 1883, citée pl. XXVI, fig. 1-3 dans l'éd. 1886)"
  },
  {
    // Lévitique 19:19 & Deutéronome 22:9 : Semailles et scènes de labour en Orient
    match: (plate, fig, ctx) =>
      ((plate === 32 || plate === 33) && (/11|12|13|15|29/.test(fig) || /semence|semer|semailles|froment.*orge/i.test(ctx))) ||
      (plate === 25 && (/4|5|7/.test(fig) && /labour|semailles/i.test(ctx))),
    targetPlate: 25,
    targetFig: '4, 5, 7',
    originalPlate: 32,
    note: "Scènes de labour et semailles dans l'antique Égypte (éd. 1883, citée pl. XXXII / XXXIII, fig. 11-13, 15 dans l'éd. 1886)"
  },
  {
    // Lévitique 19:26-27 : Coupe des cheveux et de la barbe chez les peuples voisins d'Israël
    match: (plate, fig, ctx) =>
      ((plate === 86 || plate === 116) && (/cheveux|barbe|tond|rotund/i.test(ctx))) ||
      (plate === 4 && fig === '3'),
    targetPlate: 4,
    targetFig: '3',
    originalPlate: 116,
    note: "Coupe des cheveux et de la barbe chez les peuples alliés ou ennemis des anciens Égyptiens (éd. 1883, citée pl. CXVI, fig. 12 dans l'éd. 1886)"
  },
  {
    // Lévitique 19:28 : Marques au front et tatouages rituels en Orient
    match: (plate, fig, ctx) =>
      ((plate === 7 || plate === 85 || plate === 115) && (/incision|tatouage|stigma|marques?.*front|abyssinie/i.test(ctx))) ||
      (plate === 5 && (/10|11|12|13|14|15|16/.test(fig) && /tatouage|tatou[ée]/i.test(ctx))) ||
      (plate === 90 && fig === '4'),
    targetPlate: 5,
    targetFig: '10, 11, 12',
    originalPlate: 7,
    note: "Modèles de tatouage et marques corporelles en Orient (éd. 1883, citée pl. VII, fig. 1, 2, 7 / pl. CXV, fig. 4 dans l'éd. 1886)"
  },
  {
    // Lévitique 23:40 & Apocalypse 7:9 : Palmes et bouquet dit Loulab pour la fête des Tabernacles
    match: (plate, fig, ctx) =>
      ((plate === 78 || plate === 108) && (/palme|rameau|loulab|lulab|myrte|saule|triomphe/i.test(ctx))) ||
      ((plate === 85 || plate === 87) && (/palme|loulab|lulab/i.test(ctx))),
    targetPlate: 87,
    targetFig: '3',
    originalPlate: 108,
    note: "Palmes et loulab pour la fête des Tabernacles (éd. 1883, citée pl. CVIII, fig. 3 dans l'éd. 1886)"
  },
  {
    // Lévitique 23:42 / 1 Rois 20:12 / Néhémie 8:16 / 1 Maccabées 1:21 : Cabanes de feuillage pour la fête des Tabernacles
    match: (plate, fig, ctx) =>
      ((plate === 81 || plate === 101) && (/cabane|feuillage|tabernacle|toit|hutte|tente|solennit[ée]/i.test(ctx))) ||
      (plate === 85 && (fig === '1' || /cabane|feuillage|hutte/i.test(ctx))),
    targetPlate: 85,
    targetFig: '1',
    originalPlate: 101,
    note: "Cabanes de feuillage pour la fête des Tabernacles (éd. 1883, citée pl. CI dans l'éd. 1886)"
  },
  {
    // Lévitique 25:9, Nombres 10:2 & Psaume 80:4 : Trompettes sacrées et sôfâr (corne du Jubilé)
    match: (plate, fig, ctx) =>
      ((plate === 85 || plate === 87 || plate === 104) && (/s[ôo]f[aâ]r|shophar|שׁוֹפָר|hasosrot|trompette|corne|jubil[ée]/i.test(ctx))) ||
      (plate === 77 && (fig === '2' || /trompette|s[ôo]f[aâ]r|hasosrot/i.test(ctx))),
    targetPlate: 77,
    targetFig: '2',
    originalPlate: 104,
    note: "Trompettes sacrées et sôfâr (éd. 1883, citée pl. LXXXV, fig. 2 / pl. LXXXVII, fig. 5, 8, 9-11 / pl. CIV, fig. 2, 4, 12 dans l'éd. 1886)"
  },
  {
    // Lévitique 26:30 : Cippes phéniciens et colonnes solaires dédiées à Baal
    match: (plate, fig, ctx) =>
      ((plate === 112 || plate === 116) && (/cippe|baal|colonne.*solaire|simulacr|hammanim/i.test(ctx))) ||
      (plate === 89 && fig === '10'),
    targetPlate: 89,
    targetFig: '10',
    originalPlate: 112,
    note: "Cippe phénicien représentant Baal (éd. 1883, citée pl. CXII, fig. 6 / pl. CXVI, fig. 7 dans l'éd. 1886)"
  },
  {
    // Malachie 3:3 : Fondeur / orfèvre auprès de son creuset / fourneau portatif
    match: (plate, fig, ctx) =>
      (plate === 46 && (fig === '10' || fig === '9' || /orf[èe]vre|creuset|fondeur|fondre|conflans/i.test(ctx))) ||
      (plate === 37 && (fig === '10' || fig === '9' || /orf[èe]vre|creuset|fondeur|fondre/i.test(ctx))) ||
      /orf[èe]vre.*creuset|fourneau\s+portatif|sedebit conflans/i.test(ctx),
    targetPlate: 37,
    targetFig: '10',
    originalPlate: 46,
    note: "Un orfèvre et son fourneau portatif (éd. 1883, citée pl. XLVI, fig. 10 dans l'éd. 1886)"
  },
  {
    // Malachie 3:2 : Foulons nettoyant les étoffes dans des baquets
    match: (plate, fig, ctx) =>
      (plate === 44 && (/1|3|8|9/.test(fig) || /foulon|baquet|étoffe|lessive|fullonum/i.test(ctx))) ||
      (plate === 34 && (/15|16|17/.test(fig) || /foulon/i.test(ctx))) ||
      /foulon.*baquet|nettoyer\s+les\s+[ée]toffes|herba\s+fullonum/i.test(ctx),
    targetPlate: 34,
    targetFig: '15, 16, 17',
    originalPlate: 44,
    note: "Foulons nettoyant les étoffes (éd. 1883, citée pl. XLIV, fig. 1, 3, 8, 9 dans l'éd. 1886)"
  },
  {
    // 2 Rois 25:1 / 2 Macchabées 12:13 : Tours mobiles et engins de siège assyriens
    match: (plate, fig, ctx) =>
      (plate === 92 && (fig === '3' || fig === '10' || /tour|engin|si[èe]ge|terrasse|pont/i.test(ctx) || !fig)) ||
      (plate === 74 && (fig === '9' || fig === '7' || /tour|engin|si[èe]ge|terrasse|retranchement/i.test(ctx))) ||
      /tours?\s+mobile|engins?\s+de\s+si[èe]ge/i.test(ctx),
    targetPlate: 74,
    targetFig: '9',
    originalPlate: 92,
    note: "Bélier assyrien muni d'une tour de guerre et retranchements (éd. 1883, citée pl. XCII, fig. 3, 10 dans l'éd. 1886)"
  },
  {
    // 2 Rois 25:7 : Crever les yeux des captifs (Sédécias)
    match: (plate, fig, ctx) =>
      (plate === 93 && (fig === '8' || /oeil|yeux|effodit|aveugl/i.test(ctx) || !fig)) ||
      (plate === 73 && (fig === '6' || /oeil|yeux|effodit|aveugl/i.test(ctx))) ||
      /cr[èe]ve.*yeux/i.test(ctx),
    targetPlate: 73,
    targetFig: '6',
    originalPlate: 93,
    note: "On crève les yeux à des captifs (éd. 1883, citée pl. XCIII, fig. 8 dans l'éd. 1886)"
  },
  {
    // 2 Rois 25:7 / Job 13:27 / 2 Chroniques 16:10 : Chaînes, menottes, ceps et entraves
    match: (plate, fig, ctx) =>
      (plate === 71 && (/1|3|8/.test(fig) || /cha[îi]ne|catenis|menotte|entrave|ceps|nervo|bloc/i.test(ctx) || !fig)) ||
      (plate === 56 && (/3|4|5/.test(fig) || /cha[îi]ne|catenis|menotte|entrave|ceps|nervo|bloc/i.test(ctx))),
    targetPlate: 56,
    targetFig: '4, 5',
    originalPlate: 71,
    note: "Prisonnier assyrien avec menottes et entraves (éd. 1883, citée pl. LXXI, fig. 1, 3, 8 dans l'éd. 1886)"
  },
  {
    // 2 Rois 10:8 : Têtes coupées des fils d'Achab
    match: (plate, fig, ctx) =>
      (plate === 92 && (fig === '4' || /t[êe]te/i.test(ctx))) ||
      (plate === 73 && (fig === '4' || /t[êe]te/i.test(ctx))),
    targetPlate: 73,
    targetFig: '4',
    originalPlate: 92,
    note: "On compte les têtes des ennemis vaincus (éd. 1883, citée pl. XCII, fig. 4 dans l'éd. 1886)"
  },
  {
    // 2 Rois 17:31 : Dieu-poisson Dagon
    match: (plate, fig, ctx) =>
      (plate === 111 && !/apis|b[œo]uf/i.test(ctx)) ||
      (plate === 91 && (/1|2/.test(fig) || /dieu-poisson|dagon/i.test(ctx))) ||
      /dieu-poisson|peau de poisson/i.test(ctx),
    targetPlate: 91,
    targetFig: '1',
    originalPlate: 111,
    note: "Le dieu-poisson Dagon des Assyriens (éd. 1883, citée pl. CXI, fig. 6 dans l'éd. 1886)"
  },
  {
    // 2 Rois 19:28 : Anneau à la lèvre des captifs
    match: (plate, fig, ctx) =>
      (plate === 93 && fig === '6') ||
      (plate === 74 && (fig === '5' || /anneau.*l[èe]vre/i.test(ctx))) ||
      /anneau.*l[èe]vre/i.test(ctx),
    targetPlate: 74,
    targetFig: '5',
    originalPlate: 93,
    note: "Anneau passé à la lèvre des captifs (éd. 1883, citée pl. XCIII, fig. 6 dans l'éd. 1886)"
  },
  {
    // 2 Rois 11:3, 11:11, 12:9 : Culte et temple de Salomon
    match: (plate, fig, ctx) =>
      ((plate === 97 || plate === 99) && /joas|athalie|joad|garde.*roi|piliers.*temple/i.test(ctx)) ||
      (plate === 76 && fig === '1' && /joas|athalie|joad/i.test(ctx)),
    targetPlate: 76,
    targetFig: '1',
    originalPlate: 97,
    note: "Vue générale du temple et mobilier sacré (éd. 1883, citée pl. XCVII/XCIX dans l'éd. 1886)"
  },
  {
    // 2 Rois 16:10 : Autel païen de Damas
    match: (plate, fig, ctx) =>
      ((plate === 98 || plate === 116) && /damas|pa[ïi]en|achaz|syrie/i.test(ctx)) ||
      (plate === 90 && fig === '2' && /damas|pa[ïi]en|achaz/i.test(ctx)),
    targetPlate: 90,
    targetFig: '2',
    originalPlate: 98,
    note: "Autel et sacrifice païen (éd. 1883, citée pl. XCVIII/CXVI dans l'éd. 1886)"
  },
  {
    // 1 Macchabées 9:54 : Murs de la cour intérieure du temple de Salomon
    match: (plate, fig, ctx) =>
      (plate === 96 && (fig === '1' || /parvis|temple|cour/i.test(ctx) || !fig)) ||
      (plate === 78 && (fig === '1' || /parvis|temple|cour/i.test(ctx))),
    targetPlate: 78,
    targetFig: '1',
    originalPlate: 96,
    note: "Plan du temple de Salomon et ses parvis (éd. 1883, citée pl. XCVI, fig. 1 dans l'éd. 1886)"
  },
  {
    // 1 Macchabées 14:27 : Inscription commémorative gravée sur des tables d'airain
    match: (plate, fig, ctx) =>
      ((plate === 112 || plate === 113) && /inscript|st[èe]le|comm[ée]morat|table.*airain/i.test(ctx)) ||
      (plate === 53 && /inscript|st[èe]le/i.test(ctx)),
    targetPlate: 53,
    targetFig: '1',
    originalPlate: 112,
    note: "Stèle et inscriptions commémoratives (éd. 1883, citée pl. CXII, fig. 6 / CXIII, fig. 9 dans l'éd. 1886)"
  },
  {
    // 1 Macchabées 16:10 / 2 Rois 17:9 : Tours de garde dans les champs
    match: (plate, fig, ctx) =>
      (plate === 36 && (fig === '5' || /tour.*garde|tour.*champs|turre custodum/i.test(ctx))) ||
      (plate === 72 && (fig === '1' || /tour.*garde|tour.*champs/i.test(ctx))),
    targetPlate: 72,
    targetFig: '1',
    originalPlate: 36,
    note: "Tours et remparts d'une place forte (éd. 1883, citée pl. XXXVI, fig. 5 dans l'éd. 1886)"
  },
  {
    // Exode 30:1 & 2 Macchabées 2:5 : Autel des parfums / de l'encensement
    match: (plate, fig, ctx) =>
      (plate === 104 && (fig === '2' || /duos annulos|autel.*parfum|autel.*encens|altare incensi/i.test(ctx))) ||
      (plate === 77 && (fig === '9' || /autel.*parfum|autel.*encens/i.test(ctx))),
    targetPlate: 77,
    targetFig: '9',
    originalPlate: 104,
    note: "L'autel de l'encensement (éd. 1883, citée pl. CIV, fig. 2 dans l'éd. 1886)"
  },
  {
    // Job 12:18 : Cordes servant à lier les prisonniers
    match: (plate, fig, ctx) =>
      (plate === 94 && (/1|4|6|8/.test(fig) || /corde|prisonnier|lier.*prisonnier|fune renes/i.test(ctx) || !fig)) ||
      (plate === 74 && (fig === '2' || /corde|prisonnier/i.test(ctx))),
    targetPlate: 74,
    targetFig: '2',
    originalPlate: 94,
    note: "Prisonniers de guerre attachés avec des cordes (éd. 1883, citée pl. XCIV, fig. 1, 4, 6-8 dans l'éd. 1886)"
  },
  {
    // Job 31:27 : Adoration en baisant sa main / geste d'adoration
    match: (plate, fig, ctx) =>
      (plate === 106 && (/5|6|9/.test(fig) && /ador|oscul|bais|main.*bouche/i.test(ctx))) ||
      (plate === 76 && (fig === '3' || /ador|oscul|bais/i.test(ctx))) ||
      /bais.*main|osculat.*manum|geste d'adoration/i.test(ctx),
    targetPlate: 76,
    targetFig: '3',
    originalPlate: 106,
    note: "Adoratio : geste d'adoration chez les anciens (éd. 1883, citée pl. CVI, fig. 5, 6, 9 dans l'éd. 1886)"
  },
  {
    // Psaume 17:39 / Psaume 109:1 / Deutéronome 33:29 : Vainqueurs foulant aux pieds leurs ennemis vaincus
    match: (plate, fig, ctx) =>
      (plate === 94 && (/3|6|7|8/.test(fig) || /pied|cadent subtus|foulant aux pieds|colla|calcabis|fouler|ennemi/i.test(ctx))) ||
      (plate === 65 && (/3|4|5/.test(fig) || /pied|foulant aux pieds|colla|calcabis|fouler/i.test(ctx))),
    targetPlate: 65,
    targetFig: '3',
    originalPlate: 94,
    note: "Darius et rois foulant aux pieds leurs ennemis vaincus (éd. 1883 pl. LXV fig. 3, citée pl. XCIV fig. 3, 6-8 dans l'éd. 1886)"
  },
  {
    // Nombres 23:22 / Job 39:9 / Psaume 21:22 : Licorne, rêmim, aurochs, taureau sauvage
    match: (plate, fig, ctx) =>
      ((plate === 77 || plate === 91 || plate === 92 || plate === 93 || plate === 94) && /auroch|bison|licorne|r[ée]m|rhinoc|cornes? des? r|taureau.*sauvage/i.test(ctx)) ||
      (plate === 32 && (fig === '1' || /auroch|bison|taureau.*sauvage|licorne|rhinoc/i.test(ctx))),
    targetPlate: 32,
    targetFig: '1',
    originalPlate: 92,
    note: "Chasse aux taureaux sauvages / aurochs (éd. 1883, citée pl. LXXVII, XCI, XCII, XCIII, XCIV dans l'éd. 1886)"},
  {
    // Psaume 25:6 : Autel des holocaustes et cour extérieure du tabernacle
    match: (plate, fig, ctx) =>
      ((plate === 96 || plate === 97) && /autel.*holocauste|tabernacle|cour ext[ée]rieure/i.test(ctx)) ||
      (plate === 81 && fig === '7' && /autel|holocauste|tabernacle/i.test(ctx)),
    targetPlate: 81,
    targetFig: '7',
    originalPlate: 96,
    note: "Autel des holocaustes et cour du tabernacle (éd. 1883, citée pl. XCVI, fig. 2 / pl. XCVII, fig. 6 dans l'éd. 1886)"
  },
  {
    // Psaume 27:2 : Postures de prière / mains élevées
    match: (plate, fig, ctx) =>
      ((plate === 95 || plate === 96) && /posture.*pri[èe]re|mains?[^.]*pri[èe]re|extollo manus/i.test(ctx)) ||
      (plate === 76 && (/4|5/.test(fig) || /pri[èe]re/i.test(ctx))),
    targetPlate: 76,
    targetFig: '4, 5',
    originalPlate: 95,
    note: "Postures de prière chez les Hébreux (éd. 1883, citée pl. XCV, fig. 3 / pl. XCVI, fig. 5, 6 dans l'éd. 1886)"
  },
  {
    // Psaume 27:2 : Le dbir / Saint des saints / plan du sanctuaire
    match: (plate, fig, ctx) =>
      ((plate === 95 || plate === 96 || plate === 97) && /dbir|saint des saints/i.test(ctx)) ||
      (plate === 75 && fig === '1' && /dbir|saint des saints/i.test(ctx)),
    targetPlate: 75,
    targetFig: '1',
    originalPlate: 95,
    note: "Plan du Tabernacle et son Saint des saints (dbir) (éd. 1883, citée pl. XCV/XCVI/XCVII dans l'éd. 1886)"
  },
  {
    // Nombres 7:3 : Chars couverts, litières et palanquins portés par des bœufs
    match: (plate, fig, ctx) =>
      (plate === 76 && (fig === '13' || /liti[èe]re|palanquin|plaustra|chars? couverts?|b[œo]ufs/i.test(ctx))),
    targetPlate: 58,
    targetFig: '11, 12, 13',
    originalPlate: 76,
    note: "Chars couverts et moyens de transport (éd. 1883, citée pl. LXXVI, fig. 13 dans l'éd. 1886)"
  },
  {
    // Nombres 31:50 : Periscelides, anneaux précieux et chaînettes portés au-dessus du pied
    match: (plate, fig, ctx) =>
      ((plate === 6 || plate === 7) && (/14|15|16|17/.test(fig) || /periscelid|cheville|anneau.*pied|cha[îi]nette/i.test(ctx))) ||
      (plate === 6 && (/6|7|8|9/.test(fig) && /periscelid/i.test(ctx))),
    targetPlate: 6,
    targetFig: '6, 7',
    originalPlate: 6,
    note: "Bracelets et parures orientales (éd. 1883, citée pl. VI, fig. 15-17 / pl. VII, fig. 14-15 dans l'éd. 1886)"
  },
  {
    // Psaume 31:9 & Zacharie 6:1 : Mors, bride, chevaux et chars assyriens (quadriges)
    match: (plate, fig, ctx) =>
      ((plate === 76 || plate === 77) && /mors|frein|bride|camo et freno|harnais|cheva(l|ux)|mulet|quadrig|char/i.test(ctx)) ||
      (plate === 69 && (/7|8|11/.test(fig) || /mors|frein|bride|harnais|quadrig|char/i.test(ctx))),
    targetPlate: 69,
    targetFig: '7, 8, 11',
    originalPlate: 76,
    note: "Chars et chevaux assyriens avec mors, frein et bride (éd. 1883, citée pl. LXXVI, fig. 7, 9 / pl. LXXVII dans l'éd. 1886)"
  },
  {
    // Psaume 34:3 : Hache d'armes / sagaris / haches de combat
    match: (plate, fig, ctx) =>
      ((plate === 88 || plate === 89) && /hache|sagaris|arme offensive|frameam|conclude/i.test(ctx)) ||
      (plate === 69 && fig === '5'),
    targetPlate: 69,
    targetFig: '5',
    originalPlate: 88,
    note: "Haches d'armes et de combat (éd. 1883, citée pl. LXXXVIII, fig. 8 / pl. LXXXIX, fig. 12 dans l'éd. 1886)"
  },
  {
    // Psaume 39:8-9 : Livres en rouleaux, volumina, montants de bois et étuis sacrés
    match: (plate, fig, ctx) =>
      ((plate === 67 || plate === 68 || plate === 70) && /rouleau|volumin|in capite libri|livre.*forme.*rouleau|montant de bois/i.test(ctx)) ||
      (plate === 54 && (/2|3|7|8/.test(fig) || /rouleau|livre/i.test(ctx))),
    targetPlate: 54,
    targetFig: '2, 3, 8',
    originalPlate: 67,
    note: "Rouleaux sacrés, volumina et montants de bois de l'Écriture (éd. 1883, citée pl. LXVII, fig. 8 / pl. LXVIII, fig. 1, 2, 4 / pl. LXX, fig. 2, 3 dans l'éd. 1886)"
  },
  {
    // Psaume 42:3 : Tabernacle, tentes et chambres du sanctuaire
    match: (plate, fig, ctx) =>
      (plate === 45 && (/1|5/.test(fig) || /tabernacl|tente.*sanctuaire|chambres du tabernacle/i.test(ctx))) ||
      (plate === 75 && (/1|5/.test(fig) || /plan.*tabernacle|ais du tabernacle/i.test(ctx))),
    targetPlate: 75,
    targetFig: '1, 5',
    originalPlate: 45,
    note: "Plan et charpente du Tabernacle (éd. 1883, citée pl. XLV, fig. 1, 5 dans l'éd. 1886)"
  },
  {
    // Psaumes 43:20-21 et 62:5 : Geste de l'adoration et posture de prière les mains étendues (levare manus)
    match: (plate, fig, ctx) =>
      ((plate === 95 || plate === 96) && (/adoration|geste.*(adoration|pri[èe]re)|expandimus|levare|mains.*pri[èe]re|mains.*[ée]tendues/i.test(ctx))) ||
      (plate === 76 && (/3|4|5/.test(fig) || /adoratio|postures? de pri[èe]re/i.test(ctx))),
    targetPlate: 76,
    targetFig: '3, 4, 5',
    originalPlate: 95,
    note: "Adoratio et postures de prière les mains étendues (éd. 1883, citée pl. XCV, fig. 3 et pl. XCVI, fig. 5-6 dans l'éd. 1886)"
  },
  {
    // Psaume 44:2 : Calame, encrier et style du scribe rapide
    match: (plate, fig, ctx) =>
      ((plate === 68 || plate === 70) && (/5|1/.test(fig) || /calam|scribe|velociter|roseau.*[ée]criture|style/i.test(ctx))) ||
      (plate === 52 && (/2|7|8/.test(fig) || /calam|encrier|scribe|style/i.test(ctx))),
    targetPlate: 52,
    targetFig: '2, 7, 8',
    originalPlate: 68,
    note: "Encrier, calame et scribe (éd. 1883, citée pl. LXVIII, fig. 5 / pl. LXX, fig. 1 dans l'éd. 1886)"
  },
  {
    // Psaume 44:6 / Deutéronome 32:23 : Flèches aiguës, archers, carquois et assaillants
    match: (plate, fig, ctx) =>
      ((plate === 89 || plate === 94 || plate === 86) && (/5|7|11/.test(fig) || /sagitt|fl[èe]che|archers?|carquois|populi sub te|cuisse|gibb[ôo]r|guerri[èe]r/i.test(ctx))) ||
      (plate === 68 && (/9|10|11/.test(fig) && /archers?|sagitt|fl[èe]che|carquois/i.test(ctx))),
    targetPlate: 68,
    targetFig: '11',
    originalPlate: 86,
    note: "Archers, flèches de combat et carquois (éd. 1883 pl. LXVIII fig. 11, citée pl. LXXXVI fig. 11 / pl. LXXXIX / pl. XCIV dans l'éd. 1886)"
  },
  {
    // Psaume 45:1 : Musique du temple, chant des Lévites et chœur religieux
    match: (plate, fig, ctx) =>
      (plate === 61 && (/15/.test(fig) || /musique.*temple|l[ée]vite|soprano|arcanis|alamoth/i.test(ctx))) ||
      (plate === 49 && (/3/.test(fig) && /concert religieux|musique/i.test(ctx))),
    targetPlate: 49,
    targetFig: '3',
    originalPlate: 61,
    note: "Concert religieux et musique sacrée (éd. 1883, citée pl. LXI, fig. 15 dans l'éd. 1886)"
  },
  {
    // Psaume 55:9 : Outres pour conserver les liquides (larmes dans ton outre)
    match: (plate, fig, ctx) =>
      (plate === 20 && (/10|13|14|15|17/.test(fig) || /outre|liquide|larmes/i.test(ctx))) ||
      (plate === 15 && (/5|6|7|8|10/.test(fig) || /outre/i.test(ctx))),
    targetPlate: 15,
    targetFig: '6, 8',
    originalPlate: 20,
    note: "Outres pour liquides et eau (éd. 1883, citée pl. XX, fig. 10, 13, 14, 15, 17 dans l'éd. 1886)"
  },
  {
    // Psaume 57:5-6 : Charmeurs de serpents et psylles (aspic sourd / veneficus incantans)
    match: (plate, fig, ctx) =>
      (plate === 115 && (/15|16/.test(fig) || /charmeur|serpent|psylle|aspic|incantan/i.test(ctx))) ||
      (plate === 93 && (fig === '2' || /charmeur|psylle/i.test(ctx))),
    targetPlate: 93,
    targetFig: '2',
    originalPlate: 115,
    note: "Psylle ou charmeur de serpents dans l'ancienne Égypte (éd. 1883, citée pl. CXV, fig. 15, 16 dans l'éd. 1886)"
  },
  {
    // Psaume 67:26 : Jeunes filles jouant du tambourin et danse sacrée en procession
    match: (plate, fig, ctx) =>
      ((plate === 105 || plate === 61 || plate === 60) && (/9|15|7|12|14|16/.test(fig) || /tambourin|tympan|danse|cort[èe]ge|procession|jouant/i.test(ctx))) ||
      (plate === 46 && (/6|7|8|10/.test(fig) || /tambourin|danse/i.test(ctx))),
    targetPlate: 46,
    targetFig: '10',
    originalPlate: 105,
    note: "Danseuse et tambourin antique (éd. 1883, citée pl. CV, fig. 9 / pl. LXI dans l'éd. 1886)"
  },
  {
    // Psaume 71:9 : Geste de l'adoration
    match: (plate, fig, ctx) =>
      (plate === 96 && (fig === '7' || /adoration|geste.*adoration/i.test(ctx))) ||
      (plate === 76 && (fig === '3' || /adoratio|adoration/i.test(ctx))),
    targetPlate: 76,
    targetFig: '3',
    originalPlate: 96,
    note: "Adoratio et geste d'adoration (éd. 1883, citée pl. XCVI, fig. 7 dans l'éd. 1886)"
  },
  {
    // Psaume 73:4 : Étendards militaires déployés / Nombres 21:8 : Étendards et drapeaux égyptiens
    match: (plate, fig, ctx) =>
      ((plate === 85 || plate === 86 || plate === 88 || plate === 89) && (/12|13|2|4/.test(fig) || /[ée]tendard|enseigne|drapeau|signa sua/i.test(ctx))) ||
      (plate === 67 && (/2|3|4|5/.test(fig) || /[ée]tendard|enseigne|drapeau/i.test(ctx))),
    targetPlate: 67,
    targetFig: '2, 4',
    originalPlate: 86,
    note: "Étendards et enseignes militaires (éd. 1883 pl. LXVII, fig. 2, 4, citée pl. LXXXV, LXXXVI, LXXXVIII, LXXXIX dans l'éd. 1886)"
  },
  {
    // Psaume 73:5 : Hache et cognée
    match: (plate, fig, ctx) =>
      ((plate === 45 || plate === 46 || plate === 85) && (/5|9|3|1/.test(fig) || /hache|cogn[ée]e|securis|ascia/i.test(ctx))) ||
      (plate === 69 && (fig === '5' || /hache/i.test(ctx))),
    targetPlate: 69,
    targetFig: '5',
    originalPlate: 45,
    note: "Haches d'armes et instruments tranchants (éd. 1883, citée pl. XLV, fig. 5 / pl. XLVI / pl. LXXXV dans l'éd. 1886)"
  },
  {
    // Matthieu 2:2 : Geste de l'adoration et prostration orientale devant les rois et la divinité
    match: (plate, fig, ctx) =>
      ((plate === 79 || plate === 96) && (/adorare|adoration|prostration|orientaux|mages|prostern/i.test(ctx))) ||
      (plate === 76 && (/2|3/.test(fig) && /adoration|prostration/i.test(ctx))),
    targetPlate: 76,
    targetFig: '2, 3',
    originalPlate: 96,
    note: "Prostration et geste d'adoration des Orientaux (éd. 1883, citée pl. LXXIX, fig. 4, 9 et pl. XCVI, fig. 7 dans l'éd. 1886)"
  },
  {
    // Exode 25:22 & Psaume 79:2 & Hébreux 9:1-5 : Siégeant sur les Chérubins / Arche d'alliance et Chérubins égyptiens
    match: (plate, fig, ctx) =>
      ((plate === 101 || plate === 102 || plate === 103) && (/ch[ée]rubin|arche|qui sedes super cherubim|sancta sanctorum|obumbrantia/i.test(ctx))) ||
      (plate === 79 && (/arche.*juifs|ch[ée]rubins/i.test(ctx) || (/2|3|4|5|6|7/.test(fig) && /arche|alliance|sanct/i.test(ctx)))),
    targetPlate: 79,
    targetFig: '2, 3, 4, 5, 6, 7',
    originalPlate: 102,
    note: "L'arche de l'alliance et les chérubins (éd. 1883, citée pl. CII, fig. 5-6 / pl. CIII, fig. 1-6 dans l'éd. 1886)"
  },
  {
    // Psaume 80:3 : Concert sacré assyrien (lyre, cymbale, tambourin)
    match: (plate, fig, ctx) =>
      ((plate === 60 || plate === 61 || plate === 63) && (/14|16|17|1|2|7|9|5|10|12/.test(fig) || /lyre|cymbale|tambourin|concert.*assyrien/i.test(ctx))) ||
      (plate === 48 && (/12|13/.test(fig) || /lyristes assyriens/i.test(ctx))),
    targetPlate: 48,
    targetFig: '12, 13',
    originalPlate: 60,
    note: "Musiciens et lyristes assyriens (éd. 1883, citée pl. LX, LXI, LXIII dans l'éd. 1886)"
  },
  {
    // Judith 15:15 / Exode 15:20 / Juges 11:34 : Danse triomphale des femmes avec tambourins
    match: (plate, fig, ctx) =>
      ((plate === 89 || plate === 61 || plate === 60) && (/f[êe]te.*triomphale|femme|jeune.*fille|danse|tambourin|cum mulieribus/i.test(ctx))) ||
      (plate === 46 && (/10|11|12|2/.test(fig) && /danse|tambourin|femme/i.test(ctx))),
    targetPlate: 46,
    targetFig: '10',
    originalPlate: 89,
    note: "Danse triomphale et tambourins des femmes (éd. 1883, citée pl. LXXXIX, fig. 2 dans l'éd. 1886)"
  },
  {
    // Joël 2:5 / 1 Rois 4:26 / 2 Rois 7:14 : Chars de guerre
    match: (plate, fig, ctx) =>
      ((plate === 89 || plate === 69) && (/char.*guerre|chemin.*raboteux|montagne|sonitus.*quadriganum|sonitus.*flamm|joel|joël|attel[ée]|chevaux.*par.*char/i.test(ctx))),
    targetPlate: 69,
    targetFig: '7, 8',
    originalPlate: 89,
    note: "Chars de guerre assyriens (éd. 1883, citée pl. LXXXIX, fig. 2, 5, 7, 8, 12 dans l'éd. 1886)"
  },
  {
    // Jonas 3:6 : Costumes et ornements royaux assyriens (vêtement d'apparat du roi de Ninive)
    match: (plate, fig, ctx) =>
      ((plate === 81 || plate === 82 || plate === 63 || plate === 64) && (/v[êe]tement.*royal|apparat|roi.*assyrie|monarque|ninive/i.test(ctx))),
    targetPlate: 63,
    targetFig: '2, 3, 8',
    originalPlate: 81,
    note: "Costumes et ornements royaux assyriens (éd. 1883, citée pl. LXXXI, fig. 7, 8, 9 / pl. LXXXII, fig. 3 dans l'éd. 1886)"
  },
  {
    // Cantique 1:8, 1:9 : Équipages et harnais des coursiers de Pharaon
    match: (plate, fig, ctx) =>
      (plate === 77 && (/coursier|cheval|orn[ée]|harnais/i.test(ctx))) ||
      (plate === 69 && (/coursier|cheval|orn[ée]|harnais/i.test(ctx))),
    targetPlate: 69,
    targetFig: '6, 9',
    originalPlate: 77,
    note: "Harnais et ornements de coursiers (Cantique 1:8-9, éd. 1883)"
  },
  {
    // Cantique 3:7 : Palanquin ou litière de Salomon (lectulum Salomonis)
    match: (plate, fig, ctx) =>
      (plate === 78 && (/liti[èe]re|lectulum/i.test(ctx))),
    targetPlate: 59,
    targetFig: '3',
    originalPlate: 78,
    note: "Palanquin et litière royale (Cantique 3:7, éd. 1883)"
  },
  {
    // Cantique 4:1 : Parure, voile et couronne de la fiancée
    match: (plate, fig, ctx) =>
      (plate === 25 && (/couronne.*mariage/i.test(ctx))),
    targetPlate: 2,
    targetFig: '4, 5',
    originalPlate: 25,
    note: "Voiles et parures de la fiancée (Cantique 4:1, éd. 1883)"
  },
  {
    // 1 Rois 8:5 / 1 Maccabées 1:21 / Psaume 97:4 : Procession de l'arche, instruments de musique
    match: (plate, fig, ctx) =>
      (plate === 100 && (/procession|arche|instrument|musique/i.test(ctx) || !fig)) ||
      (plate === 84 && (/procession|arche|musique/i.test(ctx))),
    targetPlate: 84,
    targetFig: '1, 2',
    originalPlate: 100,
    note: "Le Temple et ses parvis (éd. 1883, citée pl. C dans l'éd. 1886)"
  },
  {
    // 1 Rois 15:18 : Divinité syrienne Hadad / Ben-Hadad
    match: (plate, fig, ctx) =>
      (plate === 122 && (/15/.test(fig) || /hadad|ben-hadad|divinit[ée]|syri/i.test(ctx))) ||
      (plate === 91 && (fig === '8' || /hadad|syri/i.test(ctx))),
    targetPlate: 91,
    targetFig: '8',
    originalPlate: 122,
    note: "Divinité syrienne et culte sémitique (éd. 1883, citée pl. CXXII, fig. 15 dans l'éd. 1886)"
  },
  {
    // Ézéchiel 28:14 : Pierres de feu, parures éblouissantes assyro-chaldéennes
    match: (plate, fig, ctx) =>
      (plate === 102 && (fig === '6' || /lapidum|ignitorum|pierre.*feu/i.test(ctx))) ||
      (plate === 81 && /lapidum|pierre.*feu/i.test(ctx)),
    targetPlate: 81,
    targetFig: '6',
    originalPlate: 102,
    note: "Joyaux et parures de culte assyriens (éd. 1883, citée pl. CII, fig. 6 dans l'éd. 1886)"
  },
  {
    // Daniel 1:2 : Divinités chaldéennes et trésor du temple de Babylone
    match: (plate, fig, ctx) =>
      (plate === 115 && (/2/.test(fig) || /babylone|thesauri|inscript.*d[ée]dicatoire/i.test(ctx))) ||
      (plate === 91 && (/2/.test(fig) && /babylone|thesauri/i.test(ctx))),
    targetPlate: 91,
    targetFig: '2',
    originalPlate: 115,
    note: "Divinités et trésor du culte babylonien (éd. 1883, citée pl. CXV, fig. 2 dans l'éd. 1886)"
  },
  {
    // Daniel 7:4 : Animaux composites assyro-babyloniens (lion ailé, taureau ailé)
    match: (plate, fig, ctx) =>
      ((plate === 105 || plate === 106) && /lion|aigle|taureau|assyro-babylonien|animaux composites/i.test(ctx)) ||
      (plate === 89 && /lion|taureau|assyro-babylonien/i.test(ctx)),
    targetPlate: 43,
    targetFig: '3',
    originalPlate: 105,
    note: "Taureau-colosse ailé assyrien (éd. 1883, citée pl. CV, CVI dans l'éd. 1886)"
  },
  {
    // Osée 5:8 : Forteresses de Benjamin (Rama, Gabaa)
    match: (plate, fig, ctx) =>
      (plate === 104 && (/2|3|4/.test(fig) && /benjamin|forteresse|gabaa|fronti[èe]re/i.test(ctx))) ||
      (plate === 72 && /benjamin|forteresse|gabaa/i.test(ctx)),
    targetPlate: 72,
    targetFig: '2, 3',
    originalPlate: 104,
    note: "Forteresses et tours de guet de Benjamin (éd. 1883, citée pl. CIV, fig. 2-4 dans l'éd. 1886)"
  },
  {
    // Amos 3:14 : Cornes de l'autel
    match: (plate, fig, ctx) =>
      (plate === 95 && /corne.*autel/i.test(ctx)) ||
      (plate === 77 && /corne.*autel/i.test(ctx)),
    targetPlate: 81,
    targetFig: '7',
    originalPlate: 95,
    note: "Autel des sacrifices et ses quatre cornes (éd. 1883, citée pl. XCV / CIV dans l'éd. 1886)"
  },
  {
    // Amos 5:26 : Tente de Moloch et tabernacles d'idoles portatives
    match: (plate, fig, ctx) =>
      (plate === 100 && /moloch|tabernacle.*idole|sakkuth|kaiwan|chanteurs/i.test(ctx)) ||
      (plate === 91 && (fig === '6' || /moloch|tabernacle.*idole/i.test(ctx))),
    targetPlate: 91,
    targetFig: '3, 4',
    originalPlate: 100,
    note: "Arche d'Astarté et Moloch (éd. 1883, citée pl. C, CI dans l'éd. 1886)"
  },
  {
    // Zacharie 4:2 & 1 Maccabées 1:21 : Candélabre à sept branches du Temple
    match: (plate, fig, ctx) =>
      (plate === 100 && (/cand[ée]labre|chandelier|sept branches|candelabrum/i.test(ctx))) ||
      (plate === 77 && (fig === '11' || /cand[ée]labre|chandelier/i.test(ctx))),
    targetPlate: 77,
    targetFig: '11',
    originalPlate: 100,
    note: "Le candélabre à sept branches du Temple (éd. 1883, citée pl. C, fig. 7, 10 dans l'éd. 1886)"
  },
  {
    // Zacharie 13:6 : Incisions rituelles des faux prophètes idolâtres
    match: (plate, fig, ctx) =>
      (plate === 110 && /incision|cicatrice|blessure.*mains|proph[èe]te/i.test(ctx)) ||
      (plate === 91 && /incision|cicatrice/i.test(ctx)),
    targetPlate: 90,
    targetFig: '4',
    originalPlate: 110,
    note: "Marques et incisions rituelles chez les peuples idolâtres (éd. 1883, citée pl. CX, fig. 4 dans l'éd. 1886)"
  },
  {
    // 1 Maccabées 1:21 & 4:41 : Voile du Saint des saints
    match: (plate, fig, ctx) =>
      (plate === 96 && (fig === '2' || /voile|velum|rideau.*sanctuaire/i.test(ctx))) ||
      (plate === 75 && (fig === '2' || /voile|velum/i.test(ctx))),
    targetPlate: 75,
    targetFig: '1, 2',
    originalPlate: 96,
    note: "Plan et voile du sanctuaire (éd. 1883, citée pl. XCVI, fig. 2 dans l'éd. 1886)"
  },
  {
    // 1 Maccabées 14:49 : Parvis d'Israël et trésor du temple (in gazophylacio)
    match: (plate, fig, ctx) =>
      (plate === 99 && (/1|2/.test(fig) || /parvis.*isra[ëe]l|aerario|gazophylac/i.test(ctx))) ||
      (plate === 78 && (/1|2/.test(fig) || /parvis.*isra[ëe]l/i.test(ctx))),
    targetPlate: 78,
    targetFig: '1, 2',
    originalPlate: 99,
    note: "Parvis d'Israël et bâtiments du Temple (éd. 1883, citée pl. XCIX, fig. 1, 2 dans l'éd. 1886)"
  },
  {
    // Matthieu 5:15 : Candélabre et lampe pour éclairer la maison
    match: (plate, fig, ctx) =>
      ((plate === 18) && (/candelabr|cand[ée]labre|lampe.*maison|super candelabrum/i.test(ctx))) ||
      (plate === 14 && (/4|5|6/.test(fig) && /cand[ée]labre/i.test(ctx))),
    targetPlate: 14,
    targetFig: '4, 5, 6',
    originalPlate: 18,
    note: "Candélabres et supports de lampe (éd. 1883, citée pl. XVIII, fig. 5, 10, 11 dans l'éd. 1886)"
  },
  {
    // Matthieu 5:15 : Modius (muid / boisseau pour mesurer le blé)
    match: (plate, fig, ctx) =>
      ((plate === 66 || plate === 51) && (/modio|modius|boisseau|muid|mesurer.*bl[ée]/i.test(ctx))) ||
      (plate === 51 && (/16|18/.test(fig) && /modius|boisseau/i.test(ctx))),
    targetPlate: 51,
    targetFig: '16, 18',
    originalPlate: 66,
    note: "Mesures de blé et modius romain (éd. 1883, citée pl. LXVI, fig. 12, 14 dans l'éd. 1886)"
  },
  {
    // Matthieu 5:18 : Apex (keraia, petit trait de lettre hébraïque)
    match: (plate, fig, ctx) =>
      ((plate === 67 || plate === 52) && (/apex|iota|keraia|corne|lettre.*h[ée]bra[ïi]que/i.test(ctx))) ||
      (plate === 52 && (fig === '1' && /alphabet|h[ée]breu|lettre/i.test(ctx))),
    targetPlate: 52,
    targetFig: '1',
    originalPlate: 67,
    note: "Alphabet et caractères hébraïques (éd. 1883, citée pl. LXVII, fig. 3 dans l'éd. 1886)"
  },
  {
    // Matthieu 9:20-21 : Frange ou houppe du manteau (tsitsit)
    match: (plate, fig, ctx) =>
      (plate === 109 && (/frange|houppe|tsitsit|fimbria|vestiment/i.test(ctx) || !fig)) ||
      (plate === 88 && (/11|12/.test(fig) && /frange|manteau/i.test(ctx))),
    targetPlate: 88,
    targetFig: '11, 12',
    originalPlate: 109,
    note: "Manteau de prière et franges sacrées (tsitsit) (éd. 1883, citée pl. CIX, fig. 2, 9, 10 dans l'éd. 1886)"
  },
  {
    // Deutéronome 6:8-9 / Exode 13:16 / Matthieu 23:5 : Phylactères (tefillîn) et mézouza (m'zuzah)
    match: (plate, fig, ctx) =>
      (plate === 110 && (/mezuz|m['’]zuz|phylact|tefill|thphillin|porte|poteau/i.test(ctx) || /11|12/.test(fig))) ||
      (plate === 88 && (/6|7|8|10/.test(fig) && /phylact|tefill|prière/i.test(ctx))),
    targetPlate: 88,
    targetFig: '6, 7, 8, 10',
    originalPlate: 110,
    note: "Phylactères (tefillîn) et mézouza (m'zuzah) (éd. 1883, citée pl. CX, fig. 11-12 dans l'éd. 1886)"
  },
  {
    // Luc 1:9-11 : Autel des parfums et encensement dans le Saint
    match: (plate, fig, ctx) =>
      ((plate === 96 || plate === 97) && /autel.*parfum|encens|sanctuaire/i.test(ctx)) ||
      (plate === 77 && (fig === '9' && /autel.*parfum/i.test(ctx))),
    targetPlate: 77,
    targetFig: '9',
    originalPlate: 96,
    note: "L'autel des parfums dans le Saint (éd. 1883, citée pl. XCVI, fig. 2 dans l'éd. 1886)"
  },
  {
    // Luc 2:46 / Jean 2:13 : Parvis du temple, cour des païens et galeries
    match: (plate, fig, ctx) =>
      ((plate === 94 || plate === 99) && /cour.*pa[ïi]en|galerie|portique|docteur|temple/i.test(ctx)) ||
      (plate === 78 && /cour.*pa[ïi]en|galerie/i.test(ctx)),
    targetPlate: 84,
    targetFig: '1, 2',
    originalPlate: 94,
    note: "Plan du Temple d'Hérode, cour des païens et portiques (éd. 1883, citée pl. XCIV / XCIX dans l'éd. 1886)"
  },
  {
    // Actes 27:28-29 : Plomb de sonde et ancres de navires antiques
    match: (plate, fig, ctx) =>
      ((plate === 72 || plate === 73 || plate === 120) && /ancre|navire|sonde|cordage|plomb|puppi/i.test(ctx)) ||
      (plate === 57 && fig === '4'),
    targetPlate: 57,
    targetFig: '4',
    originalPlate: 73,
    note: "Ancres antiques et navires (éd. 1883, citée pl. LXXII, fig. 4 / LXXIII, fig. 6, 7 dans l'éd. 1886)"
  },
  {
    // Éphésiens 2:14 : Mur de clôture du sanctuaire (Soreg) et parvis du Temple d'Hérode
    match: (plate, fig, ctx) =>
      ((plate === 98 || plate === 99) && /cl[ôo]ture|soreg|mur.*s[ée]paration|parvis/i.test(ctx)) ||
      (plate === 84 && /cl[ôo]ture|soreg/i.test(ctx)),
    targetPlate: 84,
    targetFig: '1, 2',
    originalPlate: 98,
    note: "Plan du Temple d'Hérode et enceinte de séparation (soreg) (éd. 1883, citée pl. XCVIII, fig. 2 dans l'éd. 1886)"
  },
  {
    // Apocalypse 1:12 : Candélabres et lampes antiques
    match: (plate, fig, ctx) =>
      (plate === 17 && /cand[ée]labre|chandelier|lychnia|lampe/i.test(ctx)) ||
      (plate === 14 && (/4|5|6/.test(fig) || /cand[ée]labre|lampe/i.test(ctx))),
    targetPlate: 14,
    targetFig: '4, 5, 6',
    originalPlate: 17,
    note: "Candélabres et lampes antiques (éd. 1883, citée pl. XVII, fig. 5, 6, 10, 11 dans l'éd. 1886)"
  },
  {
    // Apocalypse 4:10 : Attitude de l'adoration et prostration
    match: (plate, fig, ctx) =>
      (plate === 14 && /adorat|procid|prostern/i.test(ctx)) ||
      (plate === 76 && (fig === '2' || fig === '3' || /adorat|procid/i.test(ctx))),
    targetPlate: 76,
    targetFig: '3',
    originalPlate: 14,
    note: "Adoratio et prostration devant la divinité (éd. 1883, citée pl. XIV, fig. 3 dans l'éd. 1886)"
  },
  {
    // Apocalypse 5:1 : Rouleau de parchemin écrit en dedans et en dehors
    match: (plate, fig, ctx) =>
      ((plate === 66 || plate === 68) && /rouleau|parchemin|livre|scell[ée]/i.test(ctx)) ||
      (plate === 54 && (/2|7|8/.test(fig) || /rouleau|livre/i.test(ctx))),
    targetPlate: 54,
    targetFig: '2, 8',
    originalPlate: 66,
    note: "Rouleau sacré et volume scellé (éd. 1883, citée pl. LXVI, fig. 8 / LXVIII, 1, 2 dans l'éd. 1886)"
  },
  {
    // Apocalypse 5:8 : Larges coupes (phialas)
    match: (plate, fig, ctx) =>
      ((plate === 19 || plate === 24) && /coupe|phiala|fiole/i.test(ctx)) ||
      (plate === 18 && (/5|6/.test(fig) || /coupe/i.test(ctx))),
    targetPlate: 18,
    targetFig: '5, 6',
    originalPlate: 19,
    note: "Coupes et vases antiques (éd. 1883, citée pl. XIX, fig. 2 / XXIV, 8, 10 dans l'éd. 1886)"
  },
  {
    // Apocalypse 8:2 : Trompettes de guerre (Tubæ)
    match: (plate, fig, ctx) =>
      ((plate === 86 || plate === 82) && /trompette|buccina|guerre|tub[æe]/i.test(ctx)) ||
      (plate === 70 && (/5|6|7/.test(fig) || /trompette/i.test(ctx))),
    targetPlate: 70,
    targetFig: '6, 7',
    originalPlate: 86,
    note: "Trompettes de guerre et buccinateurs (éd. 1883, citée pl. LXXXVI, fig. 2 / LXXXII dans l'éd. 1886)"
  },
  {
    // Apocalypse 8:3 & 9:13 : Autel d'or de l'encens aux quatre cornes
    match: (plate, fig, ctx) =>
      ((plate === 95 || plate === 97 || plate === 98 || plate === 104) && /autel.*or|autel.*encens|quatre cornes/i.test(ctx)) ||
      (plate === 77 && (fig === '9' || /autel.*encens/i.test(ctx))),
    targetPlate: 77,
    targetFig: '9',
    originalPlate: 104,
    note: "Autel d'or des parfums (éd. 1883, citée pl. XCVIII, fig. 6 / pl. CIV, fig. 2 dans l'éd. 1886)"
  },
  {
    // Apocalypse 11:1 : Autel de bronze des holocaustes et parvis d'Israël
    match: (plate, fig, ctx) =>
      ((plate === 98 || plate === 99) && /autel.*bronze|autel.*holocauste|parvis.*isra[ëe]l/i.test(ctx)) ||
      (plate === 81 && fig === '7' && /holocauste|bronze|parvis|sanctuaire/i.test(ctx)),
    targetPlate: 81,
    targetFig: '7',
    originalPlate: 98,
    note: "Grand autel de bronze des holocaustes dans le parvis (éd. 1883, citée pl. XCVIII, fig. 6 / pl. XCIX dans l'éd. 1886)"
  },
  {
    // Apocalypse 14:19 : Cuve gigantesque du pressoir (in lacum)
    match: (plate, fig, ctx) =>
      (plate === 36 && /cuve|pressoir|lacum|vendange/i.test(ctx)) ||
      (plate === 28 && (/4|5|8/.test(fig) || /pressoir|cuve/i.test(ctx))),
    targetPlate: 28,
    targetFig: '4, 5',
    originalPlate: 36,
    note: "Pressoirs antiques et cuve de foulage (éd. 1883, citée pl. XXXVI, fig. 8 dans l'éd. 1886)"
  },
  {
    // Apocalypse 18:9 : Signes de deuil : pleurs et lamentations
    match: (plate, fig, ctx) =>
      (plate === 26 && /pleur|poitrine|lament/i.test(ctx) && !/poussi[èe]re|cendre|t[êe]te/i.test(ctx)) ||
      (plate === 20 && (/9|10/.test(fig) || /deuil|pleureuse/i.test(ctx))),
    targetPlate: 20,
    targetFig: '9, 10',
    originalPlate: 26,
    note: "Pleureuses et manifestations de deuil (éd. 1883, citée pl. XXVI, fig. 7 dans l'éd. 1886)"
  },
  {
    // Apocalypse 18:19 : Poussière jetée sur la tête en signe de deuil
    match: (plate, fig, ctx) =>
      ((plate === 26 || plate === 27) && /poussi[èe]re|cendre|t[êe]te/i.test(ctx)) ||
      (plate === 21 && (fig === '4' || /poussi[èe]re/i.test(ctx))),
    targetPlate: 21,
    targetFig: '4',
    originalPlate: 27,
    note: "On se jette de la poussière sur la tête en signe de deuil (éd. 1883, citée pl. XXVI, fig. 8 / XXVII, 7 dans l'éd. 1886)"
  },
  {
    // Apocalypse 18:21 : Meule à moudre tournée par des animaux (mola asinaria)
    match: (plate, fig, ctx) =>
      ((plate === 21 || plate === 42) && /meule|mola asinaria/i.test(ctx)) ||
      (plate === 33 && (/5|6/.test(fig) || /mola/i.test(ctx))),
    targetPlate: 33,
    targetFig: '5, 6',
    originalPlate: 42,
    note: "Meule à moudre tournée par des animaux (mola asinaria) (éd. 1883, citée pl. XXI / XLII, fig. 3 dans l'éd. 1886)"
  }
];

/**
 * Résout une référence textuelle ou numérique d'Atlas archéologique
 * en renvoyant le numéro de planche réel (1 à 93) dans l'édition 1883.
 */
export function resolveAtlasArcheo(rawPlate, rawFig = '', context = '') {
  let p = parseInt(rawPlate, 10);
  if (isNaN(p) || p <= 0) {
    p = romanToInt(rawPlate);
  }
  const f = String(rawFig || '').trim();
  const ctx = String(context || '');

  // 1. Chercher dans les concordances spécifiques
  for (const item of SPECIFIC_CONCORDANCE) {
    if (item.match(p, f, ctx)) {
      const origPlate = (p === item.targetPlate && item.originalPlate) ? item.originalPlate : p;
      return {
        plate: item.targetPlate,
        fig: item.targetFig || f,
        roman: intToRoman(item.targetPlate),
        originalPlate: origPlate,
        originalRoman: intToRoman(origPlate) || String(rawPlate).toUpperCase(),
        note: item.note,
        isConcordance: true
      };
    }
  }

  // 2. Si le numéro de planche est valide dans l'édition 1883 (1 à 93)
  if (p >= 1 && p <= 93) {
    return {
      plate: p,
      fig: f,
      roman: intToRoman(p),
      originalPlate: p,
      originalRoman: intToRoman(p),
      note: null,
      isConcordance: false
    };
  }

  // 3. Si le numéro dépasse 93 (éd. 1886 étendue), repli intelligent sur les sections cultuelles (pl. 76-93)
  let fallback = 76;
  if (p >= 94 && p <= 118) {
    fallback = Math.min(93, Math.max(76, p - 20));
  }
  return {
    plate: fallback,
    fig: f,
    roman: intToRoman(fallback),
    originalPlate: p,
    originalRoman: intToRoman(p),
    note: `Planche ${intToRoman(fallback)} (éd. 1883, correspondant à la pl. ${intToRoman(p)} de l'éd. 1886)`,
    isConcordance: true
  };
}
